import {useCallback, useEffect} from "react";
import {useLocation, useRoutes} from "react-router-dom";
import LayoutPage from "../pages/Layout/Layout";
import CustomerPage from "@/pages/Analyze/Customer/Analyze";
import FinancePage from "@/pages/Analyze/Finance/Analyze";
import MissionPage from "@/pages/Analyze/Mission/Analyze";
import SigninPage from "@/pages/Account/Signin";
import type {RouteObject} from "react-router-dom";
import {Snackbar, Alert} from "@mui/material";
import {useGlobalActions, useToast} from "@/store/global.c";
import {useAuthInfo, useToken} from "@/store/account.c";
import {useRouter} from "@/helper/hooks";
import {CustomerListPage} from "@/pages/Customer/CustomerList";
import {PoolListPage} from "@/pages/Pool/PoolList";
import {ChanceListPage} from "@/pages/Chance/ChanceList";
import {ContractListPage} from "@/pages/Contract/ContractList";
import {PaybackedListPage} from "@/pages/Payback/PaybackedList";
import {UnpaybackListPage} from "@/pages/Payback/UnpaybackList";

import {InvoiceListPage} from "@/pages/Invoice/InvoiceList";
import KanbanPage from "@/pages/Analyze/Kanban/Kanban";
import {PlanListPage} from "@/pages/Plan/PlanList";
import {TaskListPage} from "@/pages/Task/TaskList";
import {ReportListPage} from "@/pages/Report/ReportList";
import {WorktimeListPage} from "@/pages/Worktime/WorktimeList";
import {AcceptingListPage} from "@/pages/Accepting/AcceptingList";
import {FieldworkListPage} from "@/pages/Fieldwork/FieldworkList";
import * as React from "react";
import {PaymentListPage} from "@/pages/Payment/PaymentList";
import {ProjectList} from "@/pages/Project/ProjectList";
import {TermListPage} from "@/pages/Term/TermList";
import {PatentListPage} from "@/pages/Patent/PatentList";
import {TrademarkListPage} from "@/pages/Trademark/TrademarkList";
import {CopyrightListPage} from "@/pages/Copyright/CopyrightList";
import {LetterListPage} from "@/pages/Patent/LetterList";
import {CertListPage} from "@/pages/Patent/CertList";
import {ContactListPage} from "@/pages/Contact/ContactList";
import {NotFoundPage} from "@/pages/404";
import {RecordListPage} from "@/pages/Record/RecordList";
import {ForeheadListPage} from "@/pages/Forehead/ForeheadList";
import {ExpenseListPage} from "@/pages/Expense/ExpenseList";
import {ReminderListPage} from "@/pages/Patent/ReminderList";
import {MonitorListPage} from "@/pages/Patent/MonitorList";
import {SummaryListPage} from "@/pages/Summary/SummaryList";
import {LeaveListPage} from "@/pages/Leave/LeaveList";
import {UnassignedListPage} from "@/pages/Contract/UnassignedList";
import isEmpty from "lodash/isEmpty";
import {FilePreview} from "@/pages/FilePreview/FilePreview";
import {ApprovalListPage} from "@/pages/Analyze/Approval/ApprovalList";
import {MemberListPage} from "@/pages/Member/MemberList";
import {UserListPage} from "@/pages/User/UserList";
import {DeptListPage} from "@/pages/Dept/DeptList";

const RenderRouter = () => {
    const {open, message, variant, duration, severity} = useToast();
    const {setToast} = useGlobalActions();

    return (
        <>
            <Snackbar
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                open={open}
                autoHideDuration={duration! * 1000}
                onClose={() => {
                    setToast({open: false, message: ""});
                }}
            >
                <Alert severity={severity} variant={variant}>
                    {message}
                </Alert>
            </Snackbar>
            {useRoutes(routes)}
        </>
    );
};


const LayoutRouter = () => {
    const router = useRouter();
    const token = useToken();
    const auth = useAuthInfo()
    const location = useLocation();

    const check = useCallback(() => {
        if (isEmpty(token)) {
            router.push("/signin");
        } else if (location.pathname === "/") {
            router.replace(auth?.role?.route ?? "");
        }
    }, [router, token]);

    useEffect(() => {
        check();
    }, [check]);

    return <LayoutPage/>;
};


const routes: RouteObject[] = [

    {
        path: "/",
        element: <LayoutRouter/>,
        children: [
            {
                path: "/pan",
                children: [
                    {
                        path: "/pan/customer",
                        element: <CustomerPage/>,
                    },
                    {
                        path: "/pan/finance",
                        element: <FinancePage/>,
                    },
                    {
                        path: "/pan/mission",
                        element: <MissionPage/>,
                    },
                    {
                        path: "/pan/kanban",
                        element: <KanbanPage/>,
                    },
                    {
                        path: "/pan/approval",
                        element: <ApprovalListPage/>,
                    }
                ],
            },
            {
                path: "/fms",
                children: [
                    {
                        path: "/fms/paybacked/list",
                        element: <PaybackedListPage/>,
                    },
                    {
                        path: "/fms/unpayback/list",
                        element: <UnpaybackListPage/>,
                    },
                    {
                        path: "/fms/invoice/list",
                        element: <InvoiceListPage/>,
                    },
                    {
                        path: "/fms/forehead/list",
                        element: <ForeheadListPage/>,
                    },
                    {
                        path: "/fms/expense/list",
                        element: <ExpenseListPage/>,
                    },
                    {
                        path: "/fms/payment/list",
                        element: <PaymentListPage/>,
                    },
                ]
            },
            {
                path: "/crm",
                children: [
                    {
                        path: "/crm/customer/list",
                        element: <CustomerListPage/>,
                    },
                    {
                        path: "/crm/contract/list",
                        element: <ContractListPage/>,
                    },
                    {
                        path: "/crm/chance/list",
                        element: <ChanceListPage/>,
                    },
                    {
                        path: "/crm/record/list",
                        element: <RecordListPage/>,
                    },
                    {
                        path: "/crm/pool/list",
                        element: <PoolListPage/>,
                    },
                    {
                        path: "/crm/contact/list",
                        element: <ContactListPage/>,
                    },
                    {
                        path: "/crm/fieldwork/list",
                        element: <FieldworkListPage/>,
                    },
                ],
            },
            {
                path: "/mis",
                children: [
                    {
                        path: "/mis/plan/list",
                        element: <PlanListPage/>,
                    },
                    {
                        path: "/mis/task/list",
                        element: <TaskListPage/>,
                    },
                    {
                        path: "/mis/report/list",
                        element: <ReportListPage/>,
                    },
                    {
                        path: "/mis/Worktime/list",
                        element: <WorktimeListPage/>,
                    },
                    {
                        path: "/mis/accepting/list",
                        element: <AcceptingListPage/>,
                    },
                ],
            },
            {
                path: "/pj",
                children: [
                    {
                        path: "/pj/:page/list",
                        element: <ProjectList/>,
                    },
                    {
                        path: "/pj/term/list",
                        element: <TermListPage/>,
                    },
                    {
                        path: "/pj/charge/list",
                        element: <NotFoundPage/>,
                    },
                    {
                        path: "/pj/unassigned/list",
                        element: <UnassignedListPage handleType={2} code={"unassigned_project"}/>,
                    },
                ]
            },
            {
                path: "/pt",
                children: [
                    {
                        path: "/pt/:page/list",
                        element: <PatentListPage/>,
                    },
                    {
                        path: "/pt/letter/list",
                        element: <LetterListPage/>,
                    },
                    {
                        path: "/pt/cert/list",
                        element: <CertListPage/>,
                    },
                    {
                        path: "/pt/reminder/list",
                        element: <ReminderListPage/>,
                    },
                    {
                        path: "/pt/monitor/list",
                        element: <MonitorListPage/>,
                    },
                    {
                        path: "/pt/unassigned/list",
                        element: <UnassignedListPage handleType={1} code={"unassigned_case"}/>,
                    },
                ]
            },
            {
                path: "/tm",
                children: [
                    {
                        path: "/tm/:page/list",
                        element: <TrademarkListPage/>,
                    },
                    {
                        path: "/tm/unassigned/list",
                        element: <UnassignedListPage handleType={3} code={"unassigned_trademark"}/>,
                    },
                    {
                        path: "/tm/monitor/list",
                        element: <NotFoundPage/>,
                    },
                    {
                        path: "/tm/official/list",
                        element: <NotFoundPage/>
                    }
                ]
            },
            {
                path: "/co",
                children: [
                    {
                        path: "/co/:page/list",
                        element: <CopyrightListPage/>,
                    },
                    {
                        path: "/co/unassigned/list",
                        element: <UnassignedListPage handleType={4} code={"unassigned_copyright"}/>,
                    },
                ]
            },
            {
                path: "/cms",
                children: [
                    {
                        path: "/cms/summary/list",
                        element: <SummaryListPage/>,
                    },
                    {
                        path: "/cms/leave/list",
                        element: <LeaveListPage/>,
                    },
                ]
            },
            {
                path: "/sys",
                children: [
                    {
                        path: "/sys/member/list",
                        element: <MemberListPage/>,
                    },
                    {
                        path: "/sys/user/list",
                        element: <UserListPage/>,
                    },
                    {
                        path: "/sys/dept/list",
                        element: <DeptListPage/>,
                    },
                ]
            },
            {
                path: "*",
                element: <NotFoundPage/>,
            },
        ],
    },
    {
        path: "preview",
        element: <FilePreview/>,
    },
    {
        path: "*",
        element: <NotFoundPage/>,
    },
    {
        path: "signin",
        element: <SigninPage/>,
    },
];


export default RenderRouter;
